import React from "react";
import "../App.scss";
import SConsciousApiService from "../services/SConsciousApiService";
import { useAppContext } from "./AppContext";
import { useSearchParams } from "react-router";
import { IconButton } from "@fluentui/react";
import { useNavigate } from 'react-router';

type ReceiptImageProps = {
    imageUrl?: string | null;
    imgString?: string | null;
    children?: React.ReactNode;
    onClick?: (dataUrl: string) => void;
    sourceUserId?: string;
    receiptId?: string;
};

export default function ReceiptImage({ imageUrl, imgString, children, onClick, sourceUserId, receiptId }: ReceiptImageProps) {

    const { appData } = useAppContext();
    const [searchParams] = useSearchParams();
    const imageUrlParam = searchParams.get("imageUrl");
    const navigate = useNavigate();

    // Resolve the effective URL (standalone route param takes precedence)
    const effectiveUrl = imageUrlParam || imageUrl || '';

    // Build the proxied image src synchronously — no async fetch needed.
    // If a pre-loaded data URL was passed (imgString), use it directly.
    const imageSrc: string | null = React.useMemo(() => {
        if (imgString) return imgString;
        if (!effectiveUrl) return null;
        // For split receipt copies, use the source user's ID to fetch from the original blob container
        const userId = sourceUserId || appData?.user?.userId;
        if (!userId) return null;
        let blobName: string;
        try {
            const u = new URL(effectiveUrl.split('?')[0]);
            const segs = u.pathname.replace(/^\//, '').split('/');
            blobName = segs.slice(1).join('/'); // strip container (= userId)
        } catch {
            blobName = effectiveUrl.split('/').pop() || '';
        }
        let url = `/api/getImage?userId=${encodeURIComponent(userId)}&blobName=${encodeURIComponent(blobName)}`;
        // For cross-user access (split receipts), include the receipt ID for server-side authorization
        if (sourceUserId && sourceUserId !== appData?.user?.userId && receiptId) {
            url += `&receiptId=${encodeURIComponent(receiptId)}`;
        }
        return url;
    }, [imgString, effectiveUrl, appData?.user?.userId, sourceUserId, receiptId]);

    //Init Services (still needed for delete)
    let _sConsciousApiService = new SConsciousApiService();

    const handleDelete = async () => {

        const user_id = appData?.user.userId;
        const receipt_id = "NONPROCESSEDRECEIPT"
        const documentUrl = effectiveUrl;
        const blobName = documentUrl.split("/").pop();

        if (!blobName) return;

        await _sConsciousApiService.deleteReceiptData(receipt_id, user_id, blobName).then(() => {
           // Navigate to the Receipt component
            navigate(`/History`);
        }).catch((error) => {
            console.error("Error deleting image:", error);
        });
    };

    return (

        imageSrc ? 

            imageUrlParam ?

                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                            <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                title="Delete"
                                ariaLabel="Delete"
                                onClick={handleDelete}
                                styles={{
                                    root: {
                                        backgroundColor: '#ffcc00',
                                        color: '#000',
                                        borderRadius: '50%',
                                    },
                                    rootHovered: { backgroundColor: '#e6b800', color: '#000' },
                                    rootPressed: { backgroundColor: '#ccaa00', color: '#000' },
                                }}
                            />
                        </div>
                        <img height={"100%"} width={"100%"} src={imageSrc} className="receipt-image" /> 
                    </div>

                :
            
                <div style={{ position: 'relative', width: '100%' }}>
                    <img
                        width={"100%"}
                        src={imageSrc}
                        className="receipt-image"
                        style={{ display: 'block', cursor: onClick ? 'zoom-in' : undefined }}
                        onClick={onClick ? () => onClick(imageSrc) : undefined}
                    />
                    {children}
                </div>
            
            : 
            
            <></>
    );
}